if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        // Set up before the `available` check: with no variant picked yet the
        // element starts unavailable and is filled in by `update()` later, which
        // still needs the error template and the bound refresh handler.
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.errorHtml = this.querySelector('template')?.content.firstElementChild.cloneNode(true);

        if (!this.hasAttribute('available')) return;

        this.fetchAvailability(this.dataset.variantId);
      }

      fetchAvailability(variantId) {
        if (!variantId) return;

        this.currentVariantId = variantId;

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        fetch(variantSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      /*
       * Shopify's own pickup availability answers "can this be collected here", which is true when
       * the variant would arrive by store transfer. The Storefront API also exposes the quantity
       * physically on the shelf at each location, so we re-decide the tick on that instead. Without
       * a token, or on any failure, the Liquid-rendered state is left untouched.
       */
      applyPhysicalStock(variantId) {
        const token = this.dataset.storefrontToken;
        if (!token || !variantId) return;

        this.fetchPhysicalStock(variantId, token)
          .then((quantities) => {
            if (!quantities || variantId !== this.currentVariantId) return;

            const drawer = document.querySelector('pickup-availability-drawer');
            if (!drawer) return;

            drawer.querySelectorAll('[data-pickup-status]').forEach((status) => {
              const quantity = quantities[status.dataset.locationId];
              if (quantity === undefined) return;
              status.dataset.inStock = quantity > 0 ? 'true' : 'false';
            });
          })
          .catch(() => {});
      }

      fetchPhysicalStock(variantId, token) {
        const cacheKey = `pickup-stock:${variantId}`;
        const cached = this.readStockCache(cacheKey);
        if (cached) return Promise.resolve(cached);

        const apiVersion = this.dataset.storefrontApiVersion || '2025-10';
        const query = `query PickupStock($id: ID!) {
          node(id: $id) {
            ... on ProductVariant {
              storeAvailability(first: 20) {
                nodes {
                  quantityAvailable
                  location { id }
                }
              }
            }
          }
        }`;

        return fetch(`/api/${apiVersion}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({
            query,
            variables: { id: `gid://shopify/ProductVariant/${variantId}` },
          }),
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((json) => {
            const nodes = json?.data?.node?.storeAvailability?.nodes;
            if (json?.errors || !Array.isArray(nodes)) return null;

            const quantities = {};
            nodes.forEach((node) => {
              // A missing scope returns null here. Leaving it out keeps the Liquid state.
              if (typeof node.quantityAvailable !== 'number') return;
              const locationId = node.location?.id?.split('/').pop();
              if (locationId) quantities[locationId] = node.quantityAvailable;
            });

            this.writeStockCache(cacheKey, quantities);
            return quantities;
          });
      }

      readStockCache(key) {
        try {
          const cached = sessionStorage.getItem(key);
          return cached ? JSON.parse(cached) : null;
        } catch (e) {
          return null;
        }
      }

      writeStockCache(key, quantities) {
        try {
          sessionStorage.setItem(key, JSON.stringify(quantities));
        } catch (e) {
          // Private browsing or a full quota. The request simply runs again next time.
        }
      }

      update(variant) {
        if (variant?.available) {
          this.fetchAvailability(variant.id);
        } else {
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      renderError() {
        this.innerHTML = '';
        this.appendChild(this.errorHtml);

        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();
        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');

        if (this.dataset.pickupLocateLink) {
          sectionInnerHTML
            .querySelectorAll('.pickup-availability-locate-link')
            .forEach((link) => link.setAttribute('href', this.dataset.pickupLocateLink));
        }

        document.body.appendChild(sectionInnerHTML.querySelector('pickup-availability-drawer'));
        const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
        colorClassesToApply.forEach((colorClass) => {
          document.querySelector('pickup-availability-drawer').classList.add(colorClass);
        });

        const button = this.querySelector('button');
        if (button)
          button.addEventListener('click', (evt) => {
            document.querySelector('pickup-availability-drawer').show(evt.target);
          });

        this.applyPhysicalStock(this.currentVariantId);
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('click', (evt) => {
          if (evt.target === this) this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          !target.closest('#ShowPickupAvailabilityDrawer')
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}
